import { Calendar } from '@/components/calendar/Calendar';
import { MobileCalendar } from '@/components/calendar/MobileCalendar';
import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { MonthPicker } from '@/components/shared/date/MonthPicker';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { getMonthBounds } from '@/helpers/dateHelper';
import { TeamMemberCalendarContent } from '@/pages/team/teamMember/calendar/TeamMemberCalendarContent';
import { useMySchedule } from '@/services/employee/schedule/useMySchedule';
import { FC, Suspense, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import strings from '../ess.translations';
import { MyCalendarCalendarContent } from './MyCalendarContent';
import { MyCalendarHeader } from './MyCalendarHeader';
import { useESSCalendarEvents } from './useESSCalendarEvents';

export const MyCalendar: FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const defaultDate = new Date();
    return new Date(Date.UTC(defaultDate.getUTCFullYear(), defaultDate.getUTCMonth(), 1));
  });

  const [startDate, endDate] = useMemo(() => {
    return getMonthBounds(selectedDate);
  }, [selectedDate]);

  const navigate = useNavigate();

  const data = useMySchedule(startDate, endDate);

  const [sheetDate, setSheetDate] = useState<Date | null>(null);

  const pastSelected = sheetDate && sheetDate < new Date();

  const onDayClick = useCallback((date: Date) => setSheetDate(date), []);
  const events = useESSCalendarEvents(data.schedule, onDayClick);

  const dayEvent = events.find(e => e.date.toISOString() === sheetDate?.toISOString());
  const dayShifts = dayEvent?.contextObject?.shifts;
  const hasShift = dayShifts && dayShifts.length > 0;

  const closeSheet = () => setSheetDate(null);

  const handleViewRoster = () => {
    navigate(`${sheetDate!.toISOString().slice(0, 10)}/view-roster`);
    closeSheet();
  };

  const handleSetAvailability = () => {
    navigate(`${sheetDate!.toISOString().slice(0, 10)}/my-availability`);
    closeSheet();
  };

  const handleViewShiftDetails = () => {
    navigate(`${sheetDate!.toISOString().slice(0, 10)}/shift-details`);
    closeSheet();
  };

  return (
    <PageContainer customHeader={<MyCalendarHeader />}>
      <div className="bg-white border-b-2">
        <PageSizer mode={SizerMode.PAGE}>
          <MonthPicker value={selectedDate} onChange={setSelectedDate} />
        </PageSizer>
      </div>

      <Suspense fallback={<TableLoader />}>
        <div className="lg:hidden overflow-y-hidden flex flex-col">
          <PageSizer mode={SizerMode.PAGE} className="flex-1 overflow-auto">
            <MobileCalendar monthToShow={startDate} events={events} ContentComponent={MyCalendarCalendarContent} />
          </PageSizer>
        </div>
        <div className="hidden lg:block">
          <PageSizer mode={SizerMode.PAGE}>
            <Calendar monthToShow={startDate} events={events} ContentComponent={TeamMemberCalendarContent} />
          </PageSizer>
        </div>
      </Suspense>

      {sheetDate && (
        <div className="fixed inset-0 z-40 bg-black/50" onClick={closeSheet}>
          <div
            className="absolute bottom-0 left-0 right-0 z-50 bg-white divide-y-2 rounded-t-2xl overflow-hidden text-center"
            onClick={e => e.stopPropagation()}
          >
            <button onClick={handleViewRoster} className="w-full py-4 font-semibold">
              {strings.myCalendar.viewRoster}
            </button>
            {hasShift && (
              <button onClick={handleViewShiftDetails} className="w-full py-4 font-semibold">
                {strings.myCalendar.viewShiftDetails}
              </button>
            )}
            {!pastSelected && (
              <button onClick={handleSetAvailability} className="w-full py-4 font-semibold">
                {strings.myCalendar.setAvailability}
              </button>
            )}
            <button onClick={closeSheet} className="w-full py-4 font-semibold">
              {strings.myCalendar.cancel}
            </button>
          </div>
        </div>
      )}
    </PageContainer>
  );
};
